import * as React from 'react'
// libs
import { submit } from 'redux-form'
import { batch, useDispatch, useSelector } from 'react-redux'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { CreateReviewAPIContext } from 'containers/Review/CreateReview/layers/API'
// constants
import { REVIEW_STATUSES } from 'constants/review'
import { Forms, Modals, ENTITIES, OBJECT_ENTITY_NAMES } from 'constants/index'
// hooks
import { useModalData, useShowModal } from 'hooks/index'
// redux
import { hideModal as hideModalAction } from 'store/reducers/modals'
// types
import {
  IPlan,
  IField,
  IReview,
  IPlanKpi,
  IPlanAction,
  IPlanObjective,
  LogEntityTypes,
  IObjectEntityReviewQuestion
} from 'types/graphql'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// utils
import { orderBy, isFunction } from 'lodash'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Props = React.PropsWithChildren<unknown>

export const getPropertyKeyByType = (type) => {
  switch (type) {
    case ENTITIES.PLAN: {
      return OBJECT_ENTITY_NAMES.PLAN
    }
    case ENTITIES.PLAN_KPI: {
      return OBJECT_ENTITY_NAMES.KPI
    }
    case ENTITIES.PLAN_ACTION: {
      return OBJECT_ENTITY_NAMES.ACTION
    }
    case ENTITIES.PLAN_OBJECTIVE: {
      return OBJECT_ENTITY_NAMES.OBJECTIVE
    }
  }
}

// types
export declare namespace NCreateReviewBL {
  export type TBeforeUpload = () => Promise<IReview>

  export interface IContext {
    entity: IPlan | IPlanAction | IPlanKpi | IPlanObjective
    questions: IObjectEntityReviewQuestion[]
    picklists: IField[]
    beforeUpload: TBeforeUpload
    handleCloseDrawer: () => void
    handleCreateReview: () => void
    handleFormSubmit: (data: any) => void
    handleSaveDraft: () => void
    handleSetUpdateMode: () => void
    isReview: boolean
    isReviewUpdate: boolean
    review: IReview
    submitForm: () => void
  }
}
export const CreateReviewBLContext = React.createContext<NCreateReviewBL.IContext>({} as any)

const CreateReviewBL: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch()

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { reviewStatus, createReview, updateReview } = React.useContext(CreateReviewAPIContext)

  // redux
  const {
    hideModal,
    updateModal,
    modalData: { isOpen, isUpdate, review, entity, dontReopenModal }
  } = useModalData({ modalType: Modals.REVIEW_CREATE_MODAL })

  const { showModal: showObjectiveMinimizedModal } = useShowModal({
    modalType: Modals.OBJECTIVE_MINIMIZED_MODAL
  })
  const { showModal: showActionMinimizedModal } = useShowModal({
    modalType: Modals.ACTION_MINIMIZED_MODAL
  })
  const { showModal: showKPIMinimizedModal } = useShowModal({
    modalType: Modals.KPI_MINIMIZED_MODAL
  })

  const objectEntities = useSelector(objectEntitiesSelector)

  const [isReviewUpdate, setIsReviewUpdate] = React.useState<boolean>(false)

  const objectEntity = objectEntities.find((item) => item.name === getPropertyKeyByType(entity?.entityType))
  const questions = orderBy(objectEntity?.reviewQuestions, 'position')
  const reviewFieldIds = objectEntity?.reviewFieldIds || []
  const picklists = orderBy(
    (objectEntity?.fields || []).filter((field) => reviewFieldIds.includes(field.id)),
    'position'
  )

  // methods
  const handleSetUpdateMode = () => setIsReviewUpdate(true)

  const handleCloseDrawer = async () => {
    setIsReviewUpdate(false)
    await hideModal()

    if (dontReopenModal) {
      return
    }

    if (entity.entityType === ENTITIES.PLAN_OBJECTIVE) {
      showObjectiveMinimizedModal({ id: entity.id })
    }
    if (entity.entityType === ENTITIES.PLAN_ACTION) {
      showActionMinimizedModal({ id: entity.id })
    }
    if (entity.entityType === ENTITIES.PLAN_KPI) {
      showKPIMinimizedModal({ id: entity.id })
    }
  }

  const submitForm = () => dispatch(submit(Forms.CREATE_REVIEW_FORM))

  const handleSaveDraft = () => {
    reviewStatus.current = REVIEW_STATUSES.IN_PROGRESS
    submitForm()
  }

  const handleCreateReview = () => {
    reviewStatus.current = REVIEW_STATUSES.COMPLETE
    submitForm()
  }

  const handleFormSubmit = async (data) => {
    try {
      if (isFunction(treeQueryRefetch)) {
        await treeQueryRefetch()
      }

      review.status === REVIEW_STATUSES.IN_PROGRESS || isUpdate
        ? await updateReview(data)
        : await createReview(data)

      await handleCloseDrawer()
    } catch (error) {
      console.log('CreateReviewBL.handleSubmitOrUpdate', parseGraphQLErrorMessages(error))
    }
  }

  const beforeUpload = async () => {
    const createdReview = await createReview()

    updateModal({ review: { ...review, id: createdReview.id, status: REVIEW_STATUSES.IN_PROGRESS } })

    return { ...createdReview, entityType: LogEntityTypes.Review }
  }

  React.useEffect(() => setIsReviewUpdate(isUpdate), [isUpdate])

  React.useEffect(() => {
    if (isOpen) {
      batch(() => {
        dispatch(hideModalAction(Modals.ACTION_MINIMIZED_MODAL))
        dispatch(hideModalAction(Modals.KPI_MINIMIZED_MODAL))
        dispatch(hideModalAction(Modals.OBJECTIVE_MINIMIZED_MODAL))
      })
    }
  }, [isOpen])

  const value = {
    entity,
    review,
    questions,
    picklists,
    isReview: isOpen,
    isReviewUpdate,
    handleSetUpdateMode,
    handleCloseDrawer,
    handleSaveDraft,
    handleCreateReview,
    handleFormSubmit,
    submitForm,
    beforeUpload
  }

  return <CreateReviewBLContext.Provider value={value}>{children}</CreateReviewBLContext.Provider>
}

export default CreateReviewBL
